import { ReactNode, useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Button,
  Typography as AntTypography,
  Grid,
  Drawer as AntDrawer,
  type MenuProps,
  Space,
  Dropdown,
  Avatar,
  Badge,
  Popover,
  List,
  Divider,
} from 'antd';
import * as AntIcons from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import LanguageSelector from '../components/LanguageSelector';
import Breadcrumb from '../components/Breadcrumb';
import BrandLogo from '../components/common/BrandLogo';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationRecord } from '../types/notifications';

const { Header, Sider, Content } = Layout;
const { Title } = AntTypography;

const drawerWidth = 280;

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser, signOut } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentOpenKeys, setCurrentOpenKeys] = useState<string[]>(['resourceManagement']);
  const [notificationPopoverVisible, setNotificationPopoverVisible] = useState(false);

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
      setMobileOpen(false);
    } else {
      setCollapsed(false);
      setMobileOpen(false);
    }
    
    // Set initial open keys based on current path
    const initialOpenKeys = getOpenKeys();
    setCurrentOpenKeys(initialOpenKeys);
  }, [isMobile, location.pathname]);

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/onboarding') return t('navigation.onboarding', 'Onboarding');
    if (path === '/dashboard') return t('navigation.dashboard');
    if (path === '/users') return t('users:title');
    if (path.startsWith('/resources/properties')) return t('navigation.properties');
    if (path.startsWith('/resources/assets')) return t('navigation.assets');
    if (path.startsWith('/resources/employees')) return t('navigation.employees');
    if (path.startsWith('/resources/residents')) return t('navigation.residents');
    if (path.startsWith('/resources/vendors')) return t('vendors:title');
    if (path.startsWith('/resources/facility-reservations')) return t('navigation.reservations');
    if (path === '/maintenance') return t('navigation.maintenance');
    if (path === '/community/news') return t('navigation.news');
    if (path === '/community/calendar') return t('navigation.calendar');
    if (path === '/community/guidelines') return t('navigation.guidelines');
    if (path === '/community/expenses') return t('navigation.expenses');
    if (path === '/community/messages') return t('navigation.messages');
    if (path === '/community/emergency-alerts') return t('navigation.emergencyAlerts');
    if (path === '/community/visitor-management') return t('navigation.visitorManagement');
    if (path === '/community/package-tracking') return t('navigation.packageTracking');
    if (path === '/community/lost-and-found') return t('navigation.lostAndFound');
    if (path === '/community/incidents') return t('navigation.incidentReporting');
    if (path === '/employee/attendance') return t('navigation.attendance');
    if (path === '/employee/tasks') return t('navigation.tasks');
    if (path === '/employee/maintenance-requests') return t('maintenanceRequests:title');
    if (path === '/employee/payroll') return t('payroll:title');
    if (path === '/finance/payables') return t('payables:title');
    if (path === '/finance/receivables') return t('receivables:title');
    if (path === '/finance/accounting') return t('accounting:title');
    if (path === '/documents') return t('documents:title');
    if (path === '/notifications') return t('notifications:title', 'Notifications');
    if (path === '/workspace') return t('workspace:title');
    
    // Resident pages
    if (path === '/residents/facility-reservations') return t('reservations:title');
    if (path === '/residents/package-tracking') return t('packages:title');
    if (path === '/residents/visitor-management') return t('visitors:title');
    if (path === '/residents/messages') return t('messages:title');
    if (path.startsWith('/residents/packages/')) return t('packages:package');
    if (path.startsWith('/residents/reservations/')) return t('reservations:reservation');
    if (path.startsWith('/residents/visitors/')) return t('visitors:visitor');
    
    return t('app.admin');
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const onMenuClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const mainMenuItems: MenuProps['items'] = [
    {
      key: '/onboarding',
      icon: <AntIcons.RocketOutlined />,
      label: t('navigation.onboarding', 'Onboarding'),
      onClick: () => onMenuClick('/onboarding'),
    },
    {
      key: '/dashboard',
      icon: <AntIcons.DashboardOutlined />,
      label: t('navigation.dashboard'),
      onClick: () => onMenuClick('/dashboard'),
    },
    {
      key: 'resourceManagement',
      icon: <AntIcons.AppstoreAddOutlined />,
      label: t('navigation.resourceManagement'),
      children: [
        { key: '/resources/properties', icon: <AntIcons.HomeOutlined />, label: t('navigation.properties'), onClick: () => onMenuClick('/resources/properties') },
        { key: '/resources/assets', icon: <AntIcons.ToolOutlined />, label: t('navigation.assets'), onClick: () => onMenuClick('/resources/assets') },
        { key: '/resources/employees', icon: <AntIcons.UserOutlined />, label: t('navigation.employees'), onClick: () => onMenuClick('/resources/employees') },
        { key: '/resources/residents', icon: <AntIcons.TeamOutlined />, label: t('navigation.residents'), onClick: () => onMenuClick('/resources/residents') },
        { key: '/resources/vendors', icon: <AntIcons.ShopOutlined />, label: t('navigation.vendors'), onClick: () => onMenuClick('/resources/vendors') },
      ],
    },
    {
      key: 'communityActions',
      icon: <AntIcons.UserSwitchOutlined />,
      label: t('navigation.communityActions'),
      children: [
        { key: '/community/news', icon: <AntIcons.NotificationOutlined />, label: t('navigation.news'), onClick: () => onMenuClick('/community/news') },
        { key: '/community/calendar', icon: <AntIcons.CalendarOutlined />, label: t('navigation.calendar'), onClick: () => onMenuClick('/community/calendar') },
        { key: '/community/guidelines', icon: <AntIcons.FileTextOutlined />, label: t('navigation.guidelines'), onClick: () => onMenuClick('/community/guidelines') },
        { key: '/community/expenses', icon: <AntIcons.DollarCircleOutlined />, label: t('navigation.expenses'), onClick: () => onMenuClick('/community/expenses') },
        { key: '/community/emergency-alerts', icon: <AntIcons.WarningOutlined />, label: t('navigation.emergencyAlerts'), onClick: () => onMenuClick('/community/emergency-alerts') },
        { key: '/community/lost-and-found', icon: <AntIcons.QuestionCircleOutlined />, label: t('navigation.lostAndFound'), onClick: () => onMenuClick('/community/lost-and-found') },
        { key: '/community/incidents', icon: <AntIcons.ExclamationCircleOutlined />, label: t('navigation.incidentReporting'), onClick: () => onMenuClick('/community/incidents') },
      ],
    },
    {
      key: 'residentActions',
      icon: <AntIcons.SolutionOutlined />,
      label: t('navigation.residentActions'),
      children: [
        { key: '/residents/facility-reservations', icon: <AntIcons.CalendarOutlined />, label: t('navigation.facilityReservations'), onClick: () => onMenuClick('/residents/facility-reservations') },
        { key: '/residents/visitor-management', icon: <AntIcons.ContactsOutlined />, label: t('navigation.visitorManagement'), onClick: () => onMenuClick('/residents/visitor-management') },
        { key: '/residents/package-tracking', icon: <AntIcons.GiftOutlined />, label: t('navigation.packageTracking'), onClick: () => onMenuClick('/residents/package-tracking') },
        { key: '/residents/messages', icon: <AntIcons.MessageOutlined />, label: t('navigation.messages'), onClick: () => onMenuClick('/residents/messages') },
      ],
    },
    {
      key: 'employeeActions',
      icon: <AntIcons.UserOutlined />,
      label: t('navigation.employeeActions'),
      children: [
        { key: '/employee/attendance', icon: <AntIcons.ClockCircleOutlined />, label: t('navigation.attendance'), onClick: () => onMenuClick('/employee/attendance') },
        { key: '/employee/leave-requests', icon: <AntIcons.CalendarOutlined />, label: t('navigation.leaveRequests'), onClick: () => onMenuClick('/employee/leave-requests') },
        { key: '/employee/tasks', icon: <AntIcons.ProfileOutlined />, label: t('navigation.tasks'), onClick: () => onMenuClick('/employee/tasks') },
        { key: '/employee/payroll', icon: <AntIcons.CreditCardOutlined />, label: t('navigation.payroll'), onClick: () => onMenuClick('/employee/payroll') },
      ],
    },
    {
      key: '/finance',
      icon: <AntIcons.WalletOutlined />,
      label: t('navigation.finance'),
      children: [
        { key: '/finance/payables', icon: <AntIcons.UploadOutlined style={{ transform: 'rotate(180deg)' }} />, label: t('navigation.payables'), onClick: () => onMenuClick('/finance/payables') },
        { key: '/finance/receivables', icon: <AntIcons.DownloadOutlined />, label: t('navigation.receivables'), onClick: () => onMenuClick('/finance/receivables') },
        { key: '/finance/accounting', icon: <AntIcons.SolutionOutlined />, label: t('navigation.accounting'), onClick: () => onMenuClick('/finance/accounting') },
      ]
    },
    {
      key: '/maintenance',
      icon: <AntIcons.ToolOutlined />,
      label: t('navigation.maintenance'),
      onClick: () => onMenuClick('/maintenance'),
    },
    {
      key: '/documents',
      icon: <AntIcons.FolderOutlined />,
      label: t('navigation.documents'),
      onClick: () => onMenuClick('/documents'),
    },
    {
      key: '/help',
      icon: <AntIcons.QuestionCircleOutlined />,
      label: t('help:title'),
      onClick: () => onMenuClick('/help'),
      style: { 
        background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
        color: '#ffffff',
        fontWeight: 'bold',
        borderRadius: '6px',
        margin: '2px 0',
        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)',
        border: 'none'
      }
    },
    {
      key: '/users',
      icon: <AntIcons.UserOutlined />,
      label: t('users:title'),
      onClick: () => onMenuClick('/users'),
    },
    {
      key: '/workspace',
      icon: <AntIcons.AppstoreOutlined />,
      label: t('workspace:title'),
      onClick: () => onMenuClick('/workspace'),
    },
    {
      key: '/notifications',
      icon: <AntIcons.BellOutlined />,
      label: t('notifications:title', 'Notifications'),
      onClick: () => onMenuClick('/notifications'),
    },
    {
      key: '/settings',
      icon: <AntIcons.SettingOutlined />,
      label: t('settings:title'),
      onClick: () => onMenuClick('/settings'),
    },
  ];

  const rootSubmenuKeys = mainMenuItems
    .filter((item): item is Extract<typeof item, { key: string; children: any[] }> => 
      Boolean(item && typeof item === 'object' && 'children' in item && item.children && item.key)
    )
    .map(item => item.key);

  const handleAccordionOpenChange = (newlyOpenedKeys: string[]) => {
    // Find the latest opened key that wasn't previously open
    const latestOpenKey = newlyOpenedKeys.find(key => !currentOpenKeys.includes(key));
    
    if (latestOpenKey && rootSubmenuKeys.includes(latestOpenKey)) {
      // If opening a new root submenu, close all others and open only this one
      setCurrentOpenKeys([latestOpenKey]);
    } else if (latestOpenKey) {
      // If opening a child item, find and keep the parent submenu open
      const parentKey = rootSubmenuKeys.find(key => 
        newlyOpenedKeys.includes(key)
      );
      if (parentKey) {
        setCurrentOpenKeys([parentKey]);
      } else {
        // If no parent found, just update with the new keys
        setCurrentOpenKeys(newlyOpenedKeys);
      }
    } else {
      // If closing items or no new keys, update the state accordingly
      setCurrentOpenKeys(newlyOpenedKeys);
    }
  };

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    
    // Handle resource management routes
    if (currentPath.startsWith('/resources/properties')) {
      return ['/resources/properties'];
    }
    if (currentPath.startsWith('/resources/assets')) {
      return ['/resources/assets'];
    }
    if (currentPath.startsWith('/resources/employees')) {
      return ['/resources/employees'];
    }
    if (currentPath.startsWith('/resources/residents')) {
      return ['/resources/residents'];
    }
    if (currentPath.startsWith('/resources/vendors')) {
      return ['/resources/vendors'];
    }
    
    // Handle community routes - return the specific route, not just the parent
    if (currentPath.startsWith('/community/')) {
      // Check if it's an exact match for a community route
      if (currentPath === '/community/news') return ['/community/news'];
      if (currentPath === '/community/calendar') return ['/community/calendar'];
      if (currentPath === '/community/guidelines') return ['/community/guidelines'];
      if (currentPath === '/community/expenses') return ['/community/expenses'];
      if (currentPath === '/community/emergency-alerts') return ['/community/emergency-alerts'];
      if (currentPath === '/community/lost-and-found') return ['/community/lost-and-found'];
      if (currentPath === '/community/incidents') return ['/community/incidents'];
      // Fallback to parent if no exact match
      return ['communityActions'];
    }
    
    // Handle resident routes - return the specific route, not just the parent
    if (currentPath.startsWith('/residents/')) {
      // Check if it's an exact match for a resident route
      if (currentPath === '/residents/facility-reservations') return ['/residents/facility-reservations'];
      if (currentPath === '/residents/visitor-management') return ['/residents/visitor-management'];
      if (currentPath === '/residents/package-tracking') return ['/residents/package-tracking'];
      if (currentPath === '/residents/messages') return ['/residents/messages'];
      // Fallback to parent if no exact match
      return ['residentActions'];
    }
    
    // Handle employee routes - return the specific route, not just the parent
    if (currentPath.startsWith('/employee/')) {
      // Check if it's an exact match for an employee route
      if (currentPath === '/employee/attendance') return ['/employee/attendance'];
      if (currentPath === '/employee/leave-requests') return ['/employee/leave-requests'];
      if (currentPath === '/employee/tasks') return ['/employee/tasks'];
      if (currentPath === '/employee/payroll') return ['/employee/payroll'];
      // Fallback to parent if no exact match
      return ['employeeActions'];
    }
    
    // Handle finance routes - return the specific route, not just the parent
    if (currentPath.startsWith('/finance/')) {
      // Check if it's an exact match for a finance route
      if (currentPath === '/finance/payables') return ['/finance/payables'];
      if (currentPath === '/finance/receivables') return ['/finance/receivables'];
      if (currentPath === '/finance/accounting') return ['/finance/accounting'];
      // Fallback to parent if no exact match
      return ['/finance'];
    }
    
    // Handle exact matches for main menu items
    for (const item of mainMenuItems) {
      if (item && typeof item === 'object' && 'key' in item && item.key === currentPath) {
        return [item.key];
      }
      if (item && typeof item === 'object' && 'children' in item && item.children) {
        for (const child of item.children) {
          if (child && typeof child === 'object' && 'key' in child && child.key === currentPath) {
            return [child.key];
          }
        }
      }
    }
    
    return [currentPath];
  };

  const getOpenKeys = () => {
    const currentPath = location.pathname;
    
    // If user has manually opened a submenu, respect that choice
    if (currentOpenKeys.length > 0) {
      return currentOpenKeys;
    }
    
    // Only set initial open keys based on current path if no manual interaction
    if (currentPath.startsWith('/resources/')) {
      return ['resourceManagement'];
    }
    if (currentPath.startsWith('/community/')) {
      return ['communityActions'];
    }
    if (currentPath.startsWith('/residents/')) {
      return ['residentActions'];
    }
    if (currentPath.startsWith('/employee/')) {
      return ['employeeActions'];
    }
    if (currentPath.startsWith('/finance/')) {
      return ['/finance'];
    }
    
    return [];
  };

  const drawerContent = (
    <>
      <div style={{ height: '64px', margin: '0px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#001529', flexShrink: 0 }}>
        {!isMobile && !collapsed && (
          <BrandLogo variant="sidebar" logoHeight={40} showTitle={true} showTagline={true} />
        )}
        {!isMobile && collapsed && (
          <BrandLogo variant="sidebar" logoHeight={32} showTitle={false} />
        )}
        {isMobile && (
          <BrandLogo variant="sidebar" logoHeight={40} showTitle={true} showTagline={true} />
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          openKeys={getOpenKeys()}
          onOpenChange={handleAccordionOpenChange}
          items={mainMenuItems}
        />
      </div>
    </>
  );

  const userMenuItems = [
    {
      key: 'profile',
      label: t('common:profile'),
      icon: <AntIcons.UserOutlined />,
      onClick: () => navigate('/settings'),
    },
    {
      key: 'logout',
      label: t('common:logout'),
      icon: <AntIcons.LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  // Notification popover content
  const notificationContent = (
    <div style={{ width: 320, maxHeight: 400 }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '8px 16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Title level={5} style={{ margin: 0 }}>Notifications</Title>
        {unreadCount > 0 && (
          <Button 
            type="link" 
            size="small" 
            onClick={markAllAsRead}
            style={{ padding: 0 }}
          >
            Mark all as read
          </Button>
        )}
      </div>
      
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <div style={{ 
            padding: '24px', 
            textAlign: 'center', 
            color: '#999' 
          }}>
            No notifications
          </div>
        ) : (
          <List
            dataSource={notifications.slice(0, 10)} // Show only latest 10
            renderItem={(notification: NotificationRecord) => {
              const isRead = firestoreUser?.id ? notification.readBy[firestoreUser.id] !== undefined : false;
              const isNewMessage = notification.type === 'new_message_daily';
              
              return (
                <List.Item
                  style={{
                    padding: '12px 16px',
                    backgroundColor: isRead ? 'transparent' : '#f6ffed',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onClick={() => {
                    if (!isRead) {
                      markAsRead(notification.id);
                    }
                    
                    // Navigate to action URL if available
                    if (notification.actionUrl) {
                      // Close popover first
                      setNotificationPopoverVisible(false);
                      // Then navigate
                      navigate(notification.actionUrl);
                    }
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: isNewMessage ? '#9c27b0' : 
                                        notification.priority === 'high' ? '#dc3545' :
                                        notification.priority === 'medium' ? '#ffc107' : '#28a745',
                        marginTop: 6
                      }} />
                    }
                    title={
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: isRead ? 'normal' : 'bold',
                        color: isRead ? '#666' : '#000'
                      }}>
                        {notification.title}
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#999',
                          marginBottom: 4
                        }}>
                          {notification.title}
                        </div>
                        {isNewMessage && (
                          <div style={{
                            fontSize: '11px',
                            color: '#666',
                            fontStyle: 'italic',
                            backgroundColor: '#f5f5f5',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            marginTop: 4
                          }}>
                            "{notification.title}"
                          </div>
                        )}
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#999',
                          marginTop: 4
                        }}>
                          {dayjs(notification.timestamp.toDate()).format('MMM D, h:mm A')}
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
      
      {notifications.length > 10 && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ 
            textAlign: 'center', 
            padding: '8px',
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button 
              type="link" 
              size="small"
              onClick={() => navigate('/notifications')}
            >
              View all notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <AntDrawer
          title={null}
          placement="left"
          closable={false}
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          width={drawerWidth}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {drawerContent}
        </AntDrawer>
      ) : (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={drawerWidth}
          theme="dark"
          style={{
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 1001,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {drawerContent}
        </Sider>
      )}
      <Layout className="site-layout" style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : drawerWidth) }}>
        <Header style={{ 
          padding: isMobile ? '0 8px' : '0 16px', 
          background: '#fff', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          height: isMobile ? '48px' : '64px',
          position: 'sticky',
          top: 0,
          zIndex: 1000
        }}>
          <Button
            type="text"
            icon={<AntIcons.MenuOutlined />}
            onClick={handleDrawerToggle}
            style={{ fontSize: '16px', width: isMobile ? 48 : 64, height: isMobile ? 48 : 64 }}
          />
          <Title level={4} style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>{getPageTitle()}</Title>
          <Space size={isMobile ? 8 : 16}>
            <LanguageSelector />
            
            {/* Notifications Icon with Badge */}
            <Popover
              content={notificationContent}
              title={null}
              trigger="click"
              placement="bottomRight"
              overlayStyle={{ padding: 0 }}
              open={notificationPopoverVisible}
              onOpenChange={setNotificationPopoverVisible}
            >
              <Badge count={unreadCount} size="small">
                <Button
                  type="text"
                  icon={<AntIcons.BellOutlined />}
                  style={{ 
                    fontSize: '16px', 
                    width: isMobile ? 40 : 48, 
                    height: isMobile ? 40 : 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </Badge>
            </Popover>
            
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar 
                  size={isMobile ? 'small' : 'default'} 
                  src={firestoreUser?.profilePicture?.url}
                >
                  {!firestoreUser?.profilePicture?.url && (firestoreUser?.displayName?.[0] || firestoreUser?.email?.[0])}
                </Avatar>
                {!isMobile && <span>{firestoreUser?.displayName || firestoreUser?.email}</span>}
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <div style={{ padding: '0 24px', background: '#fff' }}>
          <Breadcrumb />
        </div>
        <Content style={{ padding: 8, background: '#fff', minHeight: 280 }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
} 